/**
 * 文件验证工具函数
 * 
 * 提供与Transloadit模板一致的文件验证逻辑，用于验证上传文件的类型和大小
 */
import {
  SUPPORTED_IMAGE_FORMATS,
  SUPPORTED_TEXT_FORMATS,
  SUPPORTED_CONVERTIBLE_FORMATS,
  MAX_SIZE_IMAGE,
  MAX_SIZE_PDF,
  MAX_SIZE_TEXT,
  MAX_SIZE_CONVERTIBLE,
  MAX_SIZE_OCTET_STREAM,
  MAX_SIZE_X_APPLICATION
} from '../constants/fileUploadConstants';

/**
 * 文件类型接口
 */
export interface FileData {
  type: string;
  size: number;
  name?: string;
}

/**
 * 验证文件类型和大小
 * 
 * @param fileData 文件数据对象，包含type和size属性
 * @returns true 如果文件验证通过
 * @throws Error 如果文件验证失败，包含详细的错误信息
 */
export const validateFileTypeAndSize = (fileData: FileData): boolean => {
  const { type: fileType, size: fileSize } = fileData;

  // 检查是否为图片文件
  const isImage = /^image\//.test(fileType) && fileType !== 'image/svg+xml';
  if (isImage) {
    // 验证图片格式
    if (!SUPPORTED_IMAGE_FORMATS.includes(fileType)) {
      throw new Error('不支持的图片格式，仅支持JPEG、PNG、GIF和WebP');
    }

    // 验证图片大小
    if (fileSize > MAX_SIZE_IMAGE) {
      throw new Error(`图片文件过大，最大支持5MB，当前大小: ${(fileSize / (1024 * 1024)).toFixed(2)}MB`);
    }

    return true;
  }

  // 检查是否为PDF文件
  const isPDF = /^application\/pdf/.test(fileType);
  if (isPDF) {
    // 验证PDF大小
    if (fileSize > MAX_SIZE_PDF) {
      throw new Error(`PDF文件过大，最大支持36MB，当前大小: ${(fileSize / (1024 * 1024)).toFixed(2)}MB`);
    }

    return true;
  }

  // 检查是否为文本文件
  const isTextFile = (
    /^text\//.test(fileType) ||
    fileType === 'image/svg+xml' ||
    SUPPORTED_TEXT_FORMATS.some(type => {
      if (type.includes('*')) {
        const prefix = type.split('*')[0];
        return fileType.startsWith(prefix);
      }
      return fileType === type;
    })
  );

  if (isTextFile) {
    // 验证文本文件大小
    if (fileSize > MAX_SIZE_TEXT) {
      throw new Error(`文本文件过大，最大支持1MB，当前大小: ${(fileSize / (1024 * 1024)).toFixed(2)}MB`);
    }

    return true;
  }

  // 检查是否为可转换文档
  const isConvertible = SUPPORTED_CONVERTIBLE_FORMATS.some(type => {
    if (type.includes('*')) {
      const prefix = type.split('*')[0];
      return fileType.startsWith(prefix);
    }
    return fileType === type;
  });

  if (isConvertible) {
    // 验证可转换文档大小
    if (fileSize > MAX_SIZE_CONVERTIBLE) {
      throw new Error(`文档文件过大，最大支持36MB，当前大小: ${(fileSize / (1024 * 1024)).toFixed(2)}MB`);
    }

    return true;
  }

  // 特殊处理 application/octet-stream 和 application/x-* 小文件
  if (fileType === 'application/octet-stream' && fileSize <= MAX_SIZE_OCTET_STREAM) {
    return true;
  }

  if (/^application\/x-/.test(fileType) && fileSize <= MAX_SIZE_X_APPLICATION) {
    return true;
  }

  // 如果文件类型不在任何支持的类别中
  throw new Error(`不支持的文件类型${fileType}，请上传图片、PDF、文本文件或Office文档`);
};

/**
 * Uppy onBeforeFileAdded 回调函数
 * 
 * @param file Uppy文件对象
 * @returns true 如果文件验证通过
 * @throws Error 如果文件验证失败
 */
export const uppyBeforeFileAddedValidator = (file: any): boolean => {
  const fileData: FileData = {
    type: file.data.type,
    size: file.data.size,
    name: file.name
  };

  return validateFileTypeAndSize(fileData);
};
